import React, { useEffect, useRef, useState } from 'react';
import { lonLatToXY, xyToLonLat, svgPathForRings } from './mapProjection.mjs';

const WIDTH = 1000;
const HEIGHT = 500;

// How far in the map will go. At 12x the whole view spans about seven degrees of
// longitude, which is close enough to place a pin on a coastline.
const MIN_SPAN = WIDTH / 12;

// A drag under this many pixels is a click. Without it every attempt to pan
// would also drop a pin wherever the finger came down.
const DRAG_SLOP = 4;

/*
 * Pointer capture keeps a drag alive when the cursor leaves the map, but it
 * throws if the pointer is no longer active — and an unhandled throw here aborts
 * the handler, which silently broke placing a guess by clicking. It is a
 * convenience, so failing to get it must not cost anything.
 */
function capture(element, pointerId, take) {
  try {
    if (take) element.setPointerCapture(pointerId);
    else element.releasePointerCapture(pointerId);
  } catch {
    // No capture, no problem: the drag simply ends if the pointer leaves.
  }
}

/*
 * `pairs` is a list of {guess, actual, label}; either end may be omitted. One
 * entry is a round in play, three are the game summary. Same component either
 * way, so the projection and the pin styling cannot drift between the views.
 *
 * The map zooms on the wheel and pans on a drag, because at whole-world scale a
 * pixel is about forty kilometres and a guess cannot be placed more precisely
 * than the scoring can tell apart. Zooming is by viewBox rather than by CSS
 * transform so that everything drawn — coastlines, pins, the line between a
 * guess and its answer — stays in map coordinates and needs no unpicking.
 */
export default function WorldMap({ rings, pairs = [], onGuess }) {
  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const [view, setView] = useState({ x: 0, y: 0, w: WIDTH, h: HEIGHT });

  // Pins and strokes are given in map units, so they would balloon as the view
  // narrows. Scaling them back by the zoom keeps them a constant size on screen.
  const scale = view.w / WIDTH;

  const clamp = (next) => {
    const w = Math.min(WIDTH, Math.max(MIN_SPAN, next.w));
    const h = (w * HEIGHT) / WIDTH;
    return {
      w,
      h,
      x: Math.min(WIDTH - w, Math.max(0, next.x)),
      y: Math.min(HEIGHT - h, Math.max(0, next.y))
    };
  };

  const pointIn = (event) => {
    const rect = svgRef.current.getBoundingClientRect();
    return {
      x: view.x + ((event.clientX - rect.left) / rect.width) * view.w,
      y: view.y + ((event.clientY - rect.top) / rect.height) * view.h
    };
  };

  /*
   * Bound natively rather than through React's onWheel: React registers wheel
   * as a passive listener, so preventDefault() in a prop is silently ignored and
   * the page scrolls out from under the map. A trackpad pinch also arrives here,
   * as a wheel event with ctrlKey set, and would zoom the whole page.
   */
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return undefined;

    const onWheel = (event) => {
      event.preventDefault();
      const at = pointIn(event);
      const factor = Math.exp(event.deltaY * (event.ctrlKey ? 0.01 : 0.0015));
      const w = view.w * factor;
      // Hold the point under the cursor still while the view narrows around it.
      const fx = (at.x - view.x) / view.w;
      const fy = (at.y - view.y) / view.h;
      setView(clamp({ w, h: (w * HEIGHT) / WIDTH, x: at.x - fx * w, y: at.y - fy * ((w * HEIGHT) / WIDTH) }));
    };

    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, [view]);

  const onPointerDown = (event) => {
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      origin: { ...view },
      moved: false
    };
    capture(event.currentTarget, event.pointerId, true);
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag) return;

    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) < DRAG_SLOP) return;
    drag.moved = true;

    const rect = svgRef.current.getBoundingClientRect();
    setView(
      clamp({
        ...drag.origin,
        x: drag.origin.x - (dx / rect.width) * drag.origin.w,
        y: drag.origin.y - (dy / rect.height) * drag.origin.h
      })
    );
  };

  const onPointerUp = (event) => {
    const drag = dragRef.current;
    dragRef.current = null;
    capture(event.currentTarget, event.pointerId, false);
    if (!drag || drag.moved || !onGuess) return;
    const at = pointIn(event);
    onGuess(xyToLonLat(at.x, at.y, WIDTH, HEIGHT));
  };

  const projected = pairs.map(({ guess, actual, label }) => ({
    label,
    guess: guess ? lonLatToXY(guess.lon, guess.lat, WIDTH, HEIGHT) : null,
    actual: actual ? lonLatToXY(actual.lon, actual.lat, WIDTH, HEIGHT) : null
  }));

  return (
    <svg
      ref={svgRef}
      className={`sg-map${onGuess ? ' sg-map-active' : ''}`}
      viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => {
        dragRef.current = null;
      }}
      role={onGuess ? 'button' : 'img'}
      aria-label={
        onGuess
          ? 'World map. Click to place your guess, scroll to zoom, drag to pan.'
          : 'World map showing the answers. Scroll to zoom, drag to pan.'
      }
    >
      <rect width={WIDTH} height={HEIGHT} className="sg-map-sea" />
      <path d={svgPathForRings(rings, WIDTH, HEIGHT)} className="sg-map-land" fillRule="evenodd" />

      {projected.map((pair, index) => (
        <g key={index}>
          {pair.guess && pair.actual && (
            <line
              x1={pair.guess.x}
              y1={pair.guess.y}
              x2={pair.actual.x}
              y2={pair.actual.y}
              className="sg-map-link"
              strokeWidth={1.5 * scale}
              strokeDasharray={`${5 * scale} ${4 * scale}`}
            />
          )}
          {pair.guess && (
            <g className="sg-pin sg-pin-guess" transform={`translate(${pair.guess.x} ${pair.guess.y})`}>
              <circle r={7 * scale} strokeWidth={2.5 * scale} />
              <circle r={2.5 * scale} className="sg-pin-core" />
            </g>
          )}
          {pair.actual && (
            <g className="sg-pin sg-pin-actual" transform={`translate(${pair.actual.x} ${pair.actual.y})`}>
              <circle r={7 * scale} strokeWidth={2.5 * scale} />
              {pair.label != null && (
                <text className="sg-pin-label" y={-11 * scale} fontSize={13 * scale}>
                  {pair.label}
                </text>
              )}
              <circle r={2.5 * scale} className="sg-pin-core" />
            </g>
          )}
        </g>
      ))}
    </svg>
  );
}
